/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    AspectRatio,
    Box,
    Flex,
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig,
    useDisclosure,
} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import GalleryBody from './partials/gallery-body'
import GalleryModal from './partials/gallery-modal'

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})
    return (
        <Box data-testid="sf-image-gallery-skeleton" mb={{base: remCalc(10), lg: 0}}>
            <Flex flexDirection={{base: 'column-reverse', lg: 'row'}}>
                <Flex gap={remCalc(8)} flexDirection={{base: 'row', lg: 'column'}}>
                    {new Array(4).fill(0).map((_, index) => (
                        <AspectRatio ratio={1} {...styles.thumbnailImageSkeleton} key={index}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    ))}
                </Flex>
                <Box width="100%" ml={{base: 0, lg: remCalc(10)}}>
                    <AspectRatio ratio={1} {...styles.heroImageSkeleton}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
            </Flex>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool,
}

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageGallery = ({
    imageGroups = [],
    powerImages = [],
    selectedVariationAttributes = {},
    lazy = false,
    videoUrl,
    subModelIdentifier = '',
}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [isModalGallerySwiper, setIsModalGallerySwiper] = useState(true)
    const [modalInitValue, setModalInitValue] = useState(0)

    const openModal = (id) => {
        setModalInitValue(id)
        onOpen()
        document.body.style.overflow = 'hidden'
    }

    const closeModal = () => {
        setIsModalGallerySwiper(true)
        onClose()
        document.body.style.overflow = 'auto'
    }

    return (
        <Flex flexDir="column" data-ab="gallery-collapsed" data-cs-label="gallery-collapsed">
            <GalleryBody
                imageGroups={imageGroups}
                selectedVariationAttributes={selectedVariationAttributes}
                lazy={lazy}
                openModal={openModal}
                variant="inPage"
                videoUrl={videoUrl}
                setIsModalGallerySwiper={setIsModalGallerySwiper}
                isModalGallerySwiper={isModalGallerySwiper}
                powerImages={powerImages}
                subModelIdentifier={subModelIdentifier}
            />
            <GalleryModal
                isOpen={isOpen}
                closeModal={closeModal}
                imageGroups={imageGroups}
                selectedVariationAttributes={selectedVariationAttributes}
                lazy={lazy}
                openModal={openModal}
                videoUrl={videoUrl}
                isModalGallerySwiper={isModalGallerySwiper}
                setIsModalGallerySwiper={setIsModalGallerySwiper}
                modalInitValue={modalInitValue}
                powerImages={powerImages}
            />
        </Flex>
    )
}

ImageGallery.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    powerImages: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Image gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool,
    videoUrl: PropTypes.string,
    subModelIdentifier: PropTypes.string,
}

export default ImageGallery
